import { validateVehiclePerception } from '../controllers/VehiclePerception';
import type { BotDescriptor } from './BotController';
import type { BotAvailability } from './BotAvailability';

export const BOT_CATALOG_PROTOCOL_VERSION = 1;

export type BotDefinition = BotDescriptor & Readonly<{ availability?: Exclude<BotAvailability, 'hidden'> }>;

export interface BotCatalogSnapshot {
  readonly version: typeof BOT_CATALOG_PROTOCOL_VERSION;
  readonly revision: string;
  readonly bots: readonly BotDefinition[];
}

const RANK_TIER_ORDER = Object.freeze({ Bronze: 0, Silver: 1, Gold: 2, Platinum: 3, Diamond: 4, Champion: 5,
  'Grand Champion': 6, SSL: 7 } as const);

let installedSnapshot: BotCatalogSnapshot = Object.freeze({
  version: BOT_CATALOG_PROTOCOL_VERSION, revision: 'unavailable', bots: Object.freeze([]),
});

export function installBotCatalogSnapshot(value: unknown): BotCatalogSnapshot {
  installedSnapshot = immutableBotCatalogSnapshot(value);
  return installedSnapshot;
}
export function currentBotCatalogSnapshot(): BotCatalogSnapshot { return installedSnapshot; }
export function configuredBotCatalog(): readonly BotDefinition[] {
  return [...installedSnapshot.bots].sort(compareBotDefinitionsByRank);
}
export function botDefinition(botId: string): BotDefinition {
  const definition = installedSnapshot.bots.find((bot) => bot.id === botId);
  if (!definition) throw new Error(`Bot "${botId}" is not available from the authoritative server.`);
  return definition;
}
export function compareBotDefinitionsByRank(left: BotDefinition, right: BotDefinition): number {
  return rankSortValue(left.approximateRank) - rankSortValue(right.approximateRank)
    || left.name.localeCompare(right.name) || left.id.localeCompare(right.id);
}

export function immutableBotCatalogSnapshot(value: unknown): BotCatalogSnapshot {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid bot catalog response.');
  const source = value as Partial<BotCatalogSnapshot>;
  if (source.version !== BOT_CATALOG_PROTOCOL_VERSION) throw new Error(`Unsupported bot catalog version: ${String(source.version)}.`);
  if (typeof source.revision !== 'string' || !source.revision.trim() || source.revision.length > 128) {
    throw new Error('Bot catalog revision is invalid.');
  }
  if (!Array.isArray(source.bots)) throw new Error('Bot catalog bots must be an array.');
  const bots = source.bots.map((bot) => immutableBotDefinition(bot));
  const ids = new Set<string>();
  for (const bot of bots) {
    if (ids.has(bot.id)) throw new Error(`Duplicate bot catalog ID "${bot.id}".`);
    ids.add(bot.id);
  }
  return Object.freeze({ version: BOT_CATALOG_PROTOCOL_VERSION, revision: source.revision, bots: Object.freeze(bots) });
}

function immutableBotDefinition(value: unknown): BotDefinition {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid bot descriptor.');
  const source = value as Partial<BotDefinition>;
  if (typeof source.id !== 'string' || !/^[a-z0-9][a-z0-9._-]{0,63}$/i.test(source.id)) throw new Error('Bot catalog ID is invalid.');
  if (typeof source.name !== 'string' || !source.name.trim() || source.name.length > 80) throw new Error(`Bot "${source.id}" name is invalid.`);
  if (typeof source.descriptionKey !== 'string' || !/^match:bots\.[A-Za-z0-9._-]+\.description$/.test(source.descriptionKey)) {
    throw new Error(`Bot "${source.id}" description key is invalid.`);
  }
  if (source.runtime !== 'remote') throw new Error(`Bot "${source.id}" must execute remotely.`);
  if (source.approximateRank !== undefined
    && (typeof source.approximateRank !== 'string' || !Number.isFinite(rankSortValue(source.approximateRank)))) {
    throw new Error(`Bot "${source.id}" has an unsupported competitive rank.`);
  }
  const policy = source.policy;
  if (!policy || typeof policy !== 'object') throw new Error(`Bot "${source.id}" policy is invalid.`);
  if (!['1v1', '2v2', '3v3', 'multi-player', 'not-applicable'].includes(policy.trainingFormat)) {
    throw new Error(`Bot "${source.id}" training format is invalid.`);
  }
  if (!Number.isSafeInteger(policy.decisionIntervalTicks) || policy.decisionIntervalTicks < 1) {
    throw new Error(`Bot "${source.id}" decision interval is invalid.`);
  }
  const decisionLeadTicks = policy.decisionLeadTicks ?? 0;
  const actionApplicationLeadTicks = policy.actionApplicationLeadTicks ?? 0;
  for (const [name, ticks] of [['decisionLeadTicks', decisionLeadTicks], ['actionApplicationLeadTicks', actionApplicationLeadTicks]] as const) {
    if (!Number.isSafeInteger(ticks) || ticks < 0 || ticks >= policy.decisionIntervalTicks) throw new Error(`Bot "${source.id}" ${name} is invalid.`);
  }
  if (policy.roundResetMode !== undefined && policy.roundResetMode !== 'preserve-policy-state' && policy.roundResetMode !== 'reset-policy-state') {
    throw new Error(`Bot "${source.id}" round reset mode is invalid.`);
  }
  if (policy.perception) validateVehiclePerception(policy.perception);
  if (source.availability !== undefined && source.availability !== 'available' && source.availability !== 'coming-soon') {
    throw new Error(`Bot "${source.id}" availability is invalid.`);
  }
  return Object.freeze({
    id: source.id, name: source.name.trim(), descriptionKey: source.descriptionKey,
    availability: source.availability ?? 'available',
    ...(source.approximateRank ? { approximateRank: source.approximateRank } : {}), runtime: 'remote',
    policy: Object.freeze({
      trainingFormat: policy.trainingFormat, decisionIntervalTicks: policy.decisionIntervalTicks,
      ...(policy.decisionLeadTicks !== undefined ? { decisionLeadTicks: policy.decisionLeadTicks } : {}),
      ...(policy.actionApplicationLeadTicks !== undefined ? { actionApplicationLeadTicks: policy.actionApplicationLeadTicks } : {}),
      ...(policy.roundResetMode !== undefined ? { roundResetMode: policy.roundResetMode } : {}),
      ...(policy.perception ? { perception: Object.freeze({ ...policy.perception }) } : {}),
    }),
  });
}

function rankSortValue(rank: string | undefined): number {
  if (!rank) return Number.POSITIVE_INFINITY;
  const match = /^(Grand Champion|Champion|Diamond|Platinum|Gold|Silver|Bronze|SSL)(?: ([1-3]))?$/.exec(rank);
  if (!match) return Number.POSITIVE_INFINITY;
  const tier = match[1] as keyof typeof RANK_TIER_ORDER;
  const division = match[2] ? Number(match[2]) : 0;
  return RANK_TIER_ORDER[tier] * 4 + division;
}
